import { useState } from 'react';
import { Card, Text, Badge, Box, Group, Modal, Button, Divider, Stack } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';

const COMPLEXITY_COLORS = {
  S:  { bg: '#ebfbee', color: '#2f9e44', border: '#b2f2bb' },
  M:  { bg: '#e7f5ff', color: '#1971c2', border: '#a5d8ff' },
  L:  { bg: '#fff9db', color: '#e67700', border: '#ffec99' },
  XL: { bg: '#fff5f5', color: '#c92a2a', border: '#ffc9c9' },
};

const COMPLEXITY_LABELS = { S: 'Simple', M: 'Medium', L: 'Large', XL: 'Extra Large' };

function UseCaseCard({ useCase }) {
  const [opened, setOpened] = useState(false);
  const {
    name, description, complexity, customer, platform, vertical,
    integrations = [], channels = [], source_file_name, source_web_url,
  } = useCase;

  const cx = COMPLEXITY_COLORS[complexity] || COMPLEXITY_COLORS.M;
  const parsedIntegrations = typeof integrations === 'string'
    ? JSON.parse(integrations || '[]') : (integrations || []);
  const parsedChannels = typeof channels === 'string'
    ? JSON.parse(channels || '[]') : (channels || []);

  const openDoc = (e) => {
    e.stopPropagation();
    if (source_web_url) window.open(source_web_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={<Text fw={700} size="md" style={{ color: '#4263eb' }}>{name}</Text>}
        size="lg"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed" fw={500}>{customer}</Text>

          <Text size="sm" style={{ lineHeight: 1.7, color: '#212529' }}>{description}</Text>

          <Divider />

          <Group gap="xs" align="center">
            <Box style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: cx.bg, border: `1px solid ${cx.border}`,
              borderRadius: 6, padding: '2px 8px',
            }}>
              <Box style={{
                width: 20, height: 20, borderRadius: '50%',
                background: cx.bg, border: `1px solid ${cx.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 700, color: cx.color,
              }}>
                {complexity}
              </Box>
              <Text size="xs" fw={600} style={{ color: cx.color }}>
                {COMPLEXITY_LABELS[complexity] || complexity} complexity
              </Text>
            </Box>
            {vertical && <Badge size="sm" variant="light" color="indigo" style={{ textTransform: 'none' }}>{vertical}</Badge>}
            {platform && <Badge size="sm" variant="outline" color="gray" style={{ textTransform: 'none' }}>{platform}</Badge>}
          </Group>

          {parsedChannels.length > 0 && (
            <Box>
              <Text size="xs" fw={600} c="dimmed" mb={4} tt="uppercase" style={{ letterSpacing: '0.05em' }}>Channels</Text>
              <Group gap={4}>
                {parsedChannels.map((ch) => (
                  <Badge key={ch} size="xs" color="teal" variant="light" style={{ textTransform: 'none' }}>{ch}</Badge>
                ))}
              </Group>
            </Box>
          )}

          {parsedIntegrations.length > 0 && (
            <Box>
              <Text size="xs" fw={600} c="dimmed" mb={4} tt="uppercase" style={{ letterSpacing: '0.05em' }}>Integrations</Text>
              <Group gap={4}>
                {parsedIntegrations.map((i) => (
                  <Badge key={i} size="xs" variant="default" style={{ textTransform: 'none', fontWeight: 400 }}>{i}</Badge>
                ))}
              </Group>
            </Box>
          )}

          {source_file_name && (
            <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>{source_file_name}</Text>
          )}

          {source_web_url && (
            <Button
              size="sm"
              variant="light"
              color="indigo"
              leftSection={<IconExternalLink size={14} />}
              onClick={() => window.open(source_web_url, '_blank', 'noopener,noreferrer')}
              mt={4}
            >
              View source document
            </Button>
          )}
        </Stack>
      </Modal>

      <Card
        padding="md"
        radius="md"
        withBorder
        onClick={() => setOpened(true)}
        style={{
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(66,99,235,0.15)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
      >
        {/* Title row */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <Text
            size="sm"
            fw={600}
            style={{ color: '#4263eb', lineHeight: 1.3, flex: 1, marginRight: 8 }}
          >
            {name}
          </Text>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Box style={{
              width: 24, height: 24, borderRadius: '50%',
              background: cx.bg, border: `1px solid ${cx.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: cx.color, flexShrink: 0,
            }}>
              {complexity}
            </Box>
            {source_web_url && (
              <Box onClick={openDoc} title="Open source document" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <IconExternalLink size={13} color="#adb5bd" />
              </Box>
            )}
          </Box>
        </Box>

        {/* Customer */}
        <Text size="xs" c="dimmed" mb={6}>{customer}</Text>

        {/* Description — 3 lines preview, click card for full */}
        <Text size="xs" lineClamp={3} style={{ lineHeight: 1.5, flex: 1, marginBottom: 10, color: '#495057' }}>
          {description}
        </Text>

        {/* Vertical + Platform badges */}
        <Group gap={4} mb={6}>
          {vertical && (
            <Badge size="xs" variant="light" color="indigo" style={{ textTransform: 'none', fontWeight: 500 }}>
              {vertical}
            </Badge>
          )}
          {platform && (
            <Badge size="xs" variant="outline" color="gray" style={{ textTransform: 'none' }}>
              {platform}
            </Badge>
          )}
        </Group>

        {/* Integration pills */}
        {parsedIntegrations.length > 0 && (
          <Group gap={4} mb={6}>
            {parsedIntegrations.slice(0, 4).map((i) => (
              <Badge key={i} size="xs" variant="default" style={{ textTransform: 'none', fontWeight: 400 }}>
                {i}
              </Badge>
            ))}
            {parsedIntegrations.length > 4 && (
              <Badge size="xs" variant="default" color="gray">
                +{parsedIntegrations.length - 4} more
              </Badge>
            )}
          </Group>
        )}

        {/* Source filename */}
        {source_file_name && (
          <Text size="10px" c="dimmed" mt="auto" style={{ fontStyle: 'italic' }}>
            {source_file_name}
          </Text>
        )}
      </Card>
    </>
  );
}

export default UseCaseCard;
